<template>
    <Card :pageTitle="template ? template.label : 'Email Template Builder'" full-width>
        <AllowPermission :permission="PERMISSION_CODES.CMS_VIEW_EMAIL_TEMPLATE">
            <div class="builder-page">
                <div v-if="loading" class="builder-state" aria-label="Loading email builder">
                    <span class="spinner" aria-hidden="true"></span>
                    <div>
                        <h1>Preparing the email builder</h1>
                        <p>Loading the template and visual editing tools.</p>
                    </div>
                </div>

                <div v-else-if="loadError" class="builder-state error-state" role="alert">
                    <span class="material-icons">error_outline</span>
                    <div>
                        <h1>Email builder could not be loaded</h1>
                        <p>{{ loadError }}</p>
                    </div>
                    <button type="button" class="secondary-button" @click="loadTemplate">Try again</button>
                </div>

                <template v-else-if="template">
                    <header class="builder-header">
                        <div class="builder-title">
                            <router-link to="/settings/email-templates" class="back-link">
                                <span class="material-icons">arrow_back</span>
                                Templates
                            </router-link>
                            <div class="title-line">
                                <h1>{{ template.label }}</h1>
                                <span :class="['status-pill', template.isPublished ? 'published' : 'fallback']">
                                    {{ publishedLabel }}
                                </span>
                                <span v-if="hasUnsavedChanges" class="unsaved-pill">Unsaved changes</span>
                            </div>
                            <p>{{ template.description }}</p>
                        </div>
                        <div class="header-actions">
                            <button type="button" class="secondary-button" :disabled="busy" @click="goToVersions">
                                <span class="material-icons">history</span>
                                Versions
                            </button>
                            <button type="button" class="secondary-button" :disabled="busy" @click="setDevice('Desktop')">
                                <span class="material-icons">desktop_windows</span>
                                Desktop
                            </button>
                            <button type="button" class="secondary-button compact" :disabled="busy" @click="setDevice('Mobile portrait')">
                                <span class="material-icons">smartphone</span>
                                Mobile
                            </button>
                            <button v-if="canEdit" type="button" class="secondary-button" :disabled="busy" @click="saveDraft">
                                <span class="material-icons">save</span>
                                {{ saving ? "Saving..." : "Save draft" }}
                            </button>
                            <button v-if="canPublish" type="button" class="primary-button" :disabled="busy || (isMarketing && !isAdmin)" @click="publishTemplate">
                                <span class="material-icons">publish</span>
                                {{ publishing ? "Publishing..." : "Publish" }}
                            </button>
                        </div>
                    </header>

                    <div v-if="isMarketing" class="compliance-banner" role="note">
                        <span class="material-icons">gavel</span>
                        <div>
                            <strong>Marketing template</strong>
                            <span>Publishing requires an administrator — the admin's publish is the compliance approval (AGCO advertising and inducement standards review applies).</span>
                        </div>
                    </div>

                    <div v-if="!canEdit" class="read-only-banner" role="note">
                        <span class="material-icons">visibility</span>
                        You can preview this template, but your role cannot save or test changes.
                    </div>

                    <section class="control-panel" aria-label="Email settings">
                        <label class="subject-field">
                            <span>Email subject</span>
                            <input v-model="subject" type="text" maxlength="255" :disabled="!canEdit || busy" @input="markDirty" />
                        </label>
                        <label class="recipient-field">
                            <span>Test recipient</span>
                            <input v-model="testRecipient" type="email" placeholder="name@example.com" :disabled="!canEdit || busy" />
                        </label>
                        <button v-if="canEdit" type="button" class="test-button" :disabled="busy || !testRecipient.trim()" @click="sendTest">
                            <span class="material-icons">send</span>
                            {{ testing ? "Sending..." : "Send test" }}
                        </button>
                    </section>

                    <div class="placeholder-bar">
                        <div>
                            <strong>Personalization fields</strong>
                            <span>Click to copy, then paste into text or the subject.</span>
                        </div>
                        <div class="placeholder-list">
                            <button
                                v-for="placeholder in placeholderKeys"
                                :key="placeholder"
                                type="button"
                                class="placeholder-chip"
                                :title="copyPlaceholderTitle(placeholder)"
                                @click="copyPlaceholder(placeholder)"
                            >
                                {{ placeholderToken(placeholder) }}
                            </button>
                        </div>
                        <button v-if="canEdit" type="button" class="fields-toggle" @click="toggleDesigner">
                            <span class="material-icons">{{ showDesigner ? "expand_less" : "tune" }}</span>
                            {{ showDesigner ? "Hide fields" : "Manage fields" }}
                        </button>
                    </div>

                    <section v-if="showDesigner" class="designer-section" aria-label="Template fields and details">
                        <div v-if="canEdit" class="metadata-form">
                            <label class="meta-field">
                                <span>Template name</span>
                                <input v-model="meta.label" type="text" maxlength="120" />
                            </label>
                            <label class="meta-field">
                                <span>Audience</span>
                                <input v-model="meta.audience" type="text" maxlength="80" />
                            </label>
                            <label class="meta-field">
                                <span>Category</span>
                                <select v-model="meta.category">
                                    <option value="transactional">Transactional</option>
                                    <option value="operational">Operational</option>
                                    <option value="marketing">Marketing</option>
                                </select>
                            </label>
                            <button
                                type="button"
                                class="meta-save"
                                :disabled="savingMetadata || !isMetadataDirty"
                                @click="saveMetadata"
                            >
                                {{ savingMetadata ? "Saving..." : "Save details" }}
                            </button>
                        </div>
                        <FieldDesignerPanel
                            :fields="fields"
                            :catalog="catalog"
                            :can-edit="canEdit"
                            @update="onFieldsUpdate"
                        />
                    </section>

                    <div class="editor-frame">
                        <div ref="editor" class="grapes-editor"></div>
                    </div>

                    <footer class="builder-footer">
                        <div>
                            <span class="material-icons">verified_user</span>
                            <p>
                                Hosted image URLs only. Drafts are private to the dashboard; publishing requires the publish permission, and marketing templates additionally require an administrator.
                            </p>
                        </div>
                        <span v-if="template.draftUpdatedAt">Draft saved {{ formatDate(template.draftUpdatedAt) }}</span>
                    </footer>
                </template>
            </div>
        </AllowPermission>
    </Card>
</template>

<script>
import "grapesjs/dist/css/grapes.min.css"
import { mapState } from "vuex"
import Card from "../../components/UI/Card.vue"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import FieldDesignerPanel from "../../components/email-builder/FieldDesignerPanel.vue"
import EmailTemplateFetchApi from "../../utils/fetch/email-template-fetch-api"
import { checkHasPermission, PERMISSION_CODES } from "../../store/permission"
import { confirmDialog, errMsg } from "../../utils/common"

const loadEditorScript = (src, globalName) => {
    if (window[globalName]) return Promise.resolve(window[globalName])
    const existing = document.querySelector(`script[data-email-builder-src="${src}"]`)
    if (existing) {
        return new Promise((resolve, reject) => {
            existing.addEventListener("load", () => resolve(window[globalName]), { once: true })
            existing.addEventListener("error", () => reject(new Error(`Could not load ${globalName}.`)), {
                once: true,
            })
        })
    }

    return new Promise((resolve, reject) => {
        const script = document.createElement("script")
        script.src = src
        script.async = true
        script.dataset.emailBuilderSrc = src
        script.addEventListener(
            "load",
            () => {
                if (window[globalName]) resolve(window[globalName])
                else reject(new Error(`${globalName} loaded without registering its browser API.`))
            },
            { once: true }
        )
        script.addEventListener("error", () => reject(new Error(`Could not load ${globalName}.`)), {
            once: true,
        })
        document.head.appendChild(script)
    })
}

function placeholderFallbackFields(placeholders) {
    return (placeholders || []).map((key) => ({
        key,
        label: key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
        type: "text",
        source: "system",
        required: false,
        sampleValue: "",
    }))
}

export default {
    components: { Card, AllowPermission, FieldDesignerPanel },
    data() {
        return {
            PERMISSION_CODES,
            template: null,
            subject: "",
            testRecipient: "",
            fields: [],
            catalog: [],
            showDesigner: false,
            meta: { label: "", audience: "", category: "transactional" },
            savingMetadata: false,
            editor: null,
            loading: true,
            loadError: "",
            saving: false,
            testing: false,
            publishing: false,
            hasUnsavedChanges: false,
            editorReady: false,
        }
    },
    computed: {
        ...mapState("auth", ["user"]),
        templateKey() {
            return `${this.$route.params.templateKey || ""}`
        },
        isAdmin() {
            return Boolean(this.user?.isAdmin)
        },
        canEdit() {
            return checkHasPermission({
                isAdmin: this.user?.isAdmin,
                permissionId: PERMISSION_CODES.CMS_EDIT_EMAIL_TEMPLATE,
                userPermissionIds: this.user?.permissionIds || [],
            })
        },
        canPublish() {
            return checkHasPermission({
                isAdmin: this.user?.isAdmin,
                permissionId: PERMISSION_CODES.CMS_PUBLISH_EMAIL_TEMPLATE,
                userPermissionIds: this.user?.permissionIds || [],
            })
        },
        busy() {
            return this.saving || this.testing || this.publishing
        },
        publishedLabel() {
            return this.template?.isPublished
                ? `Published v${this.template.publishedVersion}`
                : "Code fallback"
        },
        placeholderKeys() {
            if (this.fields.length) return this.fields.map((field) => field.key)
            return this.template?.placeholders || []
        },
        isMarketing() {
            return this.template?.category === "marketing"
        },
        isMetadataDirty() {
            if (!this.template) return false
            return (
                this.meta.label !== this.template.label ||
                this.meta.audience !== (this.template.audience || "") ||
                this.meta.category !== this.template.category
            )
        },
    },
    async mounted() {
        this.testRecipient = this.user?.email || this.user?.email_id || ""
        window.addEventListener("beforeunload", this.handleBeforeUnload)
        await this.loadTemplate()
    },
    beforeDestroy() {
        window.removeEventListener("beforeunload", this.handleBeforeUnload)
        this.destroyEditor()
    },
    beforeRouteLeave(_to, _from, next) {
        if (!this.hasUnsavedChanges || window.confirm("Discard the unsaved email template changes?")) next()
    },
    methods: {
        async loadTemplate() {
            this.loading = true
            this.loadError = ""
            this.destroyEditor()
            try {
                this.template = await EmailTemplateFetchApi.get(this.templateKey)
                this.subject = this.template.subject || ""
                this.fields = this.template.fields || placeholderFallbackFields(this.template.placeholders)
                this.meta = {
                    label: this.template.label || "",
                    audience: this.template.audience || "",
                    category: this.template.category || "transactional",
                }
                this.loading = false
                await this.$nextTick()
                await this.initializeEditor()
                this.hasUnsavedChanges = false
            } catch (err) {
                this.loadError = errMsg(err, "Check the connection and try again.")
                this.loading = false
            }
        },
        async initializeEditor() {
            const assetBase = `${process.env.BASE_URL}email-builder`
            const grapesjs = await loadEditorScript(`${assetBase}/grapes.min.js`, "grapesjs")
            const grapesJSMJML = await loadEditorScript(
                `${assetBase}/grapesjs-mjml.min.js`,
                "grapesjs-mjml"
            )
            this.editor = grapesjs.init({
                container: this.$refs.editor,
                height: "680px",
                width: "auto",
                storageManager: false,
                fromElement: false,
                components: this.template.projectData ? undefined : this.template.mjml,
                deviceManager: {
                    devices: [
                        { id: "desktop", name: "Desktop", width: "" },
                        {
                            id: "mobilePortrait",
                            name: "Mobile portrait",
                            width: "375px",
                            widthMedia: "480px",
                        },
                    ],
                },
                plugins: [grapesJSMJML],
                pluginsOpts: {
                    [grapesJSMJML]: {
                        blocks: [
                            "mj-1-column",
                            "mj-2-columns",
                            "mj-3-columns",
                            "mj-text",
                            "mj-button",
                            "mj-image",
                            "mj-divider",
                            "mj-spacer",
                            "mj-wrapper",
                        ],
                        resetBlocks: true,
                        resetDevices: false,
                        resetStyleManager: true,
                        useCustomTheme: false,
                        imagePlaceholderSrc:
                            "https://www.highties.ca/wp-content/uploads/2024/06/cropped-HighTies_Logo_Transparent-04-1-768x196.png",
                    },
                },
                assetManager: {
                    upload: false,
                },
            })
            if (this.template.projectData) this.editor.loadProjectData(this.template.projectData)
            this.editor.on("load", () => {
                this.editorReady = true
                this.editor.clearDirtyCount()
            })
            this.editor.on("update", () => {
                if (this.editorReady && this.canEdit) this.hasUnsavedChanges = true
            })
        },
        destroyEditor() {
            if (this.editor) this.editor.destroy()
            this.editor = null
            this.editorReady = false
        },
        markDirty() {
            if (this.canEdit) this.hasUnsavedChanges = true
        },
        toggleDesigner() {
            this.showDesigner = !this.showDesigner
            if (this.showDesigner && this.catalog.length === 0) {
                this.loadCatalog()
            }
        },
        async loadCatalog() {
            try {
                this.catalog = await EmailTemplateFetchApi.listFieldCatalog()
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not load the field catalog"))
            }
        },
        onFieldsUpdate(next) {
            this.fields = next
            this.markDirty()
        },
        async saveMetadata() {
            if (!this.canEdit || this.savingMetadata || !this.isMetadataDirty) return
            this.savingMetadata = true
            try {
                const updated = await EmailTemplateFetchApi.updateMetadata(this.templateKey, {
                    label: this.meta.label.trim(),
                    audience: this.meta.audience.trim() || null,
                    category: this.meta.category,
                })
                // Metadata is independent from the loaded draft snapshot. Do
                // not adopt a newer draftRevision (or draft body/fields) that
                // another tab may have saved while this editor stayed open.
                this.template = {
                    ...this.template,
                    label: updated.label,
                    description: updated.description,
                    audience: updated.audience,
                    category: updated.category,
                }
                this.meta = {
                    label: this.template.label || "",
                    audience: this.template.audience || "",
                    category: this.template.category || "transactional",
                }
                this.$toast.success("Template details saved")
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not save the template details"))
            } finally {
                this.savingMetadata = false
            }
        },
        async promptReloadOnConcurrencyConflict(message) {
            if (!`${message || ""}`.includes("draft.concurrency_conflict")) return false
            const reload = await confirmDialog({
                title: "This template changed elsewhere",
                text: "Another editor saved a newer draft while you were working. Reload to see the latest version?",
                confirmButtonText: "Reload draft",
            })
            if (reload) await this.loadTemplate()
            return true
        },
        getDraftPayload() {
            if (!this.editor) throw new Error("The visual editor is not ready yet.")
            const mjml = this.editor.runCommand("mjml-code")
            if (!mjml || typeof mjml !== "string") throw new Error("Could not export the MJML template.")
            return {
                subject: this.subject,
                mjml,
                projectData: this.editor.getProjectData(),
                fields: this.fields,
                expectedDraftRevision: this.template?.draftRevision,
            }
        },
        async saveDraft({ quiet = false } = {}) {
            if (!this.canEdit || this.saving) return false
            this.saving = true
            try {
                const payload = this.getDraftPayload()
                this.template = await EmailTemplateFetchApi.saveDraft(this.templateKey, payload)
                this.fields = this.template.fields || this.fields
                this.hasUnsavedChanges = false
                this.editor.clearDirtyCount()
                if (!quiet) this.$toast.success("Email template draft saved")
                return true
            } catch (err) {
                const message = errMsg(err, "Could not save the email template draft")
                if (await this.promptReloadOnConcurrencyConflict(message)) return false
                this.$toast.error(message)
                return false
            } finally {
                this.saving = false
            }
        },
        async sendTest() {
            if (!this.canEdit || this.testing) return
            this.testing = true
            try {
                await EmailTemplateFetchApi.sendTest(this.templateKey, {
                    ...this.getDraftPayload(),
                    recipient: this.testRecipient.trim(),
                })
                this.$toast.success(`Test email sent to ${this.testRecipient.trim()}`)
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not send the test email"))
            } finally {
                this.testing = false
            }
        },
        async publishTemplate() {
            if (!this.canPublish || this.publishing) return
            if (this.isMarketing && !this.isAdmin) return
            const confirmed = await confirmDialog({
                title: "Publish this email template?",
                text: "New emails of this type will immediately use the published design. Welcome automation remains controlled separately.",
                confirmButtonText: "Publish template",
            })
            if (!confirmed) return
            this.publishing = true
            try {
                const saved = await this.saveDraft({ quiet: true })
                if (!saved) return
                this.template = await EmailTemplateFetchApi.publish(this.templateKey, {
                    expectedDraftRevision: this.template?.draftRevision,
                })
                this.hasUnsavedChanges = false
                this.$toast.success("Email template published")
            } catch (err) {
                const message = errMsg(err, "Could not publish the email template")
                if (await this.promptReloadOnConcurrencyConflict(message)) return
                this.$toast.error(message)
            } finally {
                this.publishing = false
            }
        },
        goToVersions() {
            this.$router.push(`/settings/email-templates/${this.templateKey}/versions`)
        },
        setDevice(device) {
            if (this.editor) this.editor.setDevice(device)
        },
        async copyPlaceholder(placeholder) {
            const value = this.placeholderToken(placeholder)
            try {
                await navigator.clipboard.writeText(value)
                this.$toast.success(`${value} copied`)
            } catch (_err) {
                this.$toast.info(`Copy this placeholder: ${value}`)
            }
        },
        placeholderToken(placeholder) {
            return `{{${placeholder}}}`
        },
        copyPlaceholderTitle(placeholder) {
            return `Copy ${this.placeholderToken(placeholder)}`
        },
        handleBeforeUnload(event) {
            if (!this.hasUnsavedChanges) return
            event.preventDefault()
            event.returnValue = ""
        },
        formatDate(value) {
            const date = new Date(value)
            return Number.isNaN(date.getTime()) ? "recently" : date.toLocaleString("en-CA")
        },
    },
}
</script>

<style lang="scss" scoped>
.builder-page { color: #173b35; }

.builder-header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 24px;
    padding: 2px 0 18px;
    border-bottom: 1px solid #dce5e1;
}

.back-link {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin-bottom: 9px;
    color: #5f746d;
    font-size: 12px;
    font-weight: 700;
    text-decoration: none;
}

.back-link .material-icons { font-size: 17px; }
.title-line { display: flex; align-items: center; flex-wrap: wrap; gap: 9px; }
.builder-title h1 { margin: 0; color: #10332d; font-size: clamp(25px, 3vw, 35px); letter-spacing: -0.6px; }
.builder-title p { max-width: 670px; margin: 7px 0 0; color: #65766f; font-size: 13px; line-height: 1.45; }

.header-actions { display: flex; align-items: center; justify-content: flex-end; flex-wrap: wrap; gap: 8px; }
.secondary-button,
.primary-button,
.test-button {
    display: inline-flex;
    min-height: 40px;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 0 13px;
    border: 1px solid #c9d6d1;
    border-radius: 6px;
    background: #fff;
    color: #173b35;
    font-size: 13px;
    font-weight: 750;
    cursor: pointer;
    transition: background-color 150ms ease, border-color 150ms ease, transform 150ms ease;
}
.primary-button,
.test-button { border-color: #123b34; background: #123b34; color: #fff; }
.secondary-button:hover:not(:disabled) { border-color: #75958b; background: #f4f8f6; }
.primary-button:hover:not(:disabled), .test-button:hover:not(:disabled) { background: #0a2d27; }
.secondary-button:active:not(:disabled), .primary-button:active:not(:disabled), .test-button:active:not(:disabled) { transform: translateY(1px); }
.secondary-button:disabled, .primary-button:disabled, .test-button:disabled { cursor: not-allowed; opacity: 0.55; }
.secondary-button .material-icons, .primary-button .material-icons, .test-button .material-icons { font-size: 18px; }

.status-pill,
.unsaved-pill { padding: 5px 9px; border-radius: 999px; font-size: 11px; font-weight: 800; }
.status-pill.published { background: #e2f1eb; color: #236047; }
.status-pill.fallback { background: #eef0f1; color: #5e6864; }
.unsaved-pill { background: #f4e7d6; color: #8a5c24; }

.read-only-banner {
    display: flex;
    align-items: center;
    gap: 9px;
    margin-top: 14px;
    padding: 11px 14px;
    border-left: 4px solid #74928a;
    background: #edf4f1;
    color: #45615a;
    font-size: 13px;
}
.read-only-banner .material-icons { font-size: 19px; }

.control-panel {
    display: grid;
    grid-template-columns: minmax(280px, 1fr) minmax(220px, 340px) auto;
    align-items: end;
    gap: 12px;
    padding: 16px 0;
}
.subject-field,
.recipient-field { display: grid; gap: 6px; }
.subject-field span,
.recipient-field span { color: #536961; font-size: 11px; font-weight: 800; letter-spacing: 0.6px; text-transform: uppercase; }
.subject-field input,
.recipient-field input {
    width: 100%;
    min-height: 42px;
    padding: 0 12px;
    border: 1px solid #cbd7d3;
    border-radius: 6px;
    background: #fff;
    color: #173b35;
    font-size: 14px;
}
.subject-field input:focus,
.recipient-field input:focus { border-color: #46786d; outline: 3px solid rgba(70, 120, 109, 0.14); }

.placeholder-bar {
    display: grid;
    grid-template-columns: minmax(180px, 245px) minmax(0, 1fr) auto;
    align-items: center;
    gap: 16px;
    padding: 13px 15px;
    border: 1px solid #dde6e2;
    border-bottom: 0;
    background: #f7faf8;
}
.placeholder-bar strong,
.placeholder-bar > div > span { display: block; }
.placeholder-bar strong { font-size: 13px; }
.placeholder-bar > div > span { margin-top: 2px; color: #70817b; font-size: 11px; }
.placeholder-list { display: flex; flex-wrap: wrap; gap: 6px; }
.placeholder-chip {
    padding: 5px 8px;
    border: 1px solid #d1dcd8;
    border-radius: 999px;
    background: #fff;
    color: #315a50;
    font-family: "SFMono-Regular", Consolas, monospace;
    font-size: 10px;
    cursor: pointer;
}
.placeholder-chip:hover { border-color: #7d9d94; background: #edf5f2; }

.fields-toggle {
    display: inline-flex;
    min-height: 36px;
    align-items: center;
    gap: 6px;
    padding: 0 12px;
    border: 1px solid #c9d6d1;
    border-radius: 7px;
    background: #fff;
    color: #173b35;
    font-size: 12px;
    font-weight: 750;
    white-space: nowrap;
    cursor: pointer;
}

.fields-toggle:hover {
    border-color: #77988e;
    background: #f5f9f7;
}

.fields-toggle .material-icons {
    font-size: 17px;
}

.designer-section {
    display: grid;
    gap: 16px;
    padding: 16px;
    border: 1px solid #dde6e2;
    border-bottom: 0;
    background: #fbfdfc;
}

.metadata-form {
    display: grid;
    grid-template-columns: minmax(200px, 1fr) minmax(150px, 0.6fr) minmax(150px, 0.6fr) auto;
    align-items: end;
    gap: 12px;
    padding-bottom: 14px;
    border-bottom: 1px solid #e4ebe8;
}

.meta-field {
    display: grid;
    gap: 6px;
}

.meta-field span {
    color: #536961;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.6px;
    text-transform: uppercase;
}

.meta-field input,
.meta-field select {
    width: 100%;
    min-height: 40px;
    padding: 0 11px;
    border: 1px solid #cbd7d3;
    border-radius: 6px;
    background: #fff;
    color: #173b35;
    font-size: 13px;
}

.meta-save {
    min-height: 40px;
    padding: 0 14px;
    border: 1px solid #123b34;
    border-radius: 6px;
    background: #123b34;
    color: #fff;
    font-size: 13px;
    font-weight: 750;
    cursor: pointer;
}

.meta-save:disabled {
    cursor: not-allowed;
    opacity: 0.55;
}

.compliance-banner {
    display: grid;
    grid-template-columns: 30px minmax(0, 1fr);
    gap: 10px;
    margin-top: 14px;
    padding: 12px 14px;
    border-left: 4px solid #b8864c;
    background: #f7f3ec;
    color: #53655f;
}

.compliance-banner > .material-icons {
    color: #9a6a32;
    font-size: 22px;
}

.compliance-banner strong,
.compliance-banner span {
    display: block;
}

.compliance-banner strong {
    margin-bottom: 2px;
    color: #173b35;
    font-size: 13px;
}

.compliance-banner span {
    font-size: 12px;
    line-height: 1.45;
}

.editor-frame { overflow: hidden; border: 1px solid #d2ddd9; border-radius: 0 0 8px 8px; background: #2c3834; }
.grapes-editor { min-height: 680px; }

.builder-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    padding: 14px 2px 0;
    color: #70807b;
    font-size: 11px;
}
.builder-footer > div { display: flex; align-items: center; gap: 8px; }
.builder-footer p { margin: 0; }
.builder-footer .material-icons { color: #7e9b92; font-size: 18px; }

.builder-state {
    display: grid;
    grid-template-columns: 44px minmax(0, 1fr) auto;
    align-items: center;
    gap: 15px;
    min-height: 260px;
    padding: 26px;
    border: 1px solid #dce5e1;
    background: #f9fbfa;
}
.builder-state h1 { margin: 0; font-size: 20px; }
.builder-state p { margin: 5px 0 0; color: #6d7d78; font-size: 13px; }
.error-state { border-color: #e6c9c2; background: #fff8f6; }
.error-state > .material-icons { color: #a34b3e; font-size: 30px; }
.spinner { width: 30px; height: 30px; border: 3px solid #d5e1dc; border-top-color: #1e5a4c; border-radius: 50%; animation: spin 900ms linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 1100px) {
    .builder-header { align-items: flex-start; flex-direction: column; }
    .header-actions { justify-content: flex-start; }
    .control-panel { grid-template-columns: minmax(0, 1fr) minmax(220px, 0.7fr); }
    .test-button { grid-column: 2; }
}

@media (max-width: 760px) {
    .control-panel,
    .placeholder-bar { grid-template-columns: 1fr; }
    .test-button { grid-column: auto; }
    .metadata-form { grid-template-columns: 1fr; }
    .secondary-button.compact { display: none; }
    .builder-footer { align-items: flex-start; flex-direction: column; }
}
</style>

<style lang="scss">
.builder-page .gjs-one-bg { background-color: #173b35; }
.builder-page .gjs-two-color { color: #e7efec; }
.builder-page .gjs-three-bg { background-color: #b8864c; color: #fff; }
.builder-page .gjs-four-color,
.builder-page .gjs-four-color-h:hover { color: #c79a61; }
.builder-page .gjs-pn-panel { font-family: Arial, Helvetica, sans-serif; }
.builder-page .gjs-block { width: calc(50% - 10px); min-height: 74px; border-radius: 4px; box-shadow: none; }
.builder-page .gjs-block-label { font-size: 11px; }
.builder-page .gjs-cv-canvas { background: #edf3f0; }
.builder-page .gjs-sm-sector-title,
.builder-page .gjs-layer-title { background: #214b43; }
</style>
